import React, { useCallback, useEffect, useState } from 'react';

import { Dispatch } from 'redux';
import styled from 'styled-components';
import { GIButton } from 'app-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Form from '@lib/wrappers/Form';
import endpoints from '@constants/configs/endpoints';
import { configs } from '@constants/configs/configs';
import DatePicker from '@lib/inputs/react-datepicker';
import UniversalInput from '@lib/wrappers/UniversalInput';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { BREAK_POINTS } from '@constants/common/breakpoints';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import ErrorMessage from '@components/common/form/errorMessage';
import { formatDateToMMDDYYYY } from '@utils/dates/dateMethods';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { validateRidpPrimary, validateRidpRba } from '@actions/ridp/ridpActionCreators';
import { submitForm } from '@actions/ssap/ssapActionCreators';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { useJSCCollector } from '@modules/ridp/hooks/useJSCCollector';
import { parseUTCDateWithFormat } from '@utils/dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY, YYYY_MM_DD } from '@utils/dates/dateFormats.utils';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import { ValidationErrorMessage } from '@components/common/form';

import { StyledIdentityHeader } from '../Layout';
import { IUserInfo } from '../../types/ridpTypes';
import { environmentSelector } from '@selectors/commonSelectors';
import { FinalDecisionCodes, ResponseCodes } from '../../types/responseCodes';
import { useHandleRIDPResponse, useHandleRIDPRBAResponse } from '../../utils/useHandleRidpResponse';

const StyledContactInformationContainer = styled.div`
    .regular-input {
        display: flex;
        align-items: center;

        &.input-mobile {
            > input {
                @media (max-width: ${BREAK_POINTS.sm}) {
                    width: 100%;
                }
            }
        }

        > label {
            font-size: 16px;
            color: ${({ theme }) => theme.gray2};
            width: 200px;
            text-align: right;
            &.required-true::after {
                margin-left: 3px;
                color: ${({ theme }) => theme.dangerMain};
            }
            @media (max-width: ${BREAK_POINTS.sm}) {
                text-align: left;
            }
        }

    }
    .small-width {
        > input {
            width: 150px;
            @media (max-width: ${BREAK_POINTS.sm}) {
                width: 100%;
            }
        }
    }

    .gi-dropdown {
        margin: 0;
        display: flex;
        margin-top: 1.5rem;

        > label {
            font-size: 16px;
            color: ${({ theme }) => theme.gray2};
            width: 200px;
            text-align: right;
            &.required-true::after {
                margin-left: 3px;
                color: ${({ theme }) => theme.dangerMain};
            }
            @media (max-width: ${BREAK_POINTS.sm}) {
                text-align: left;
            }
        }



        .input-error {
            border: ${({ theme }) => `1px solid ${theme.dangerMain}`};
        }
    }
    .gi-component {
        .small-width {
            width: 150px;
            @media (max-width: ${BREAK_POINTS.sm}) {
                width: 100%;
            }
        }
        .react-datepicker-wrapper {
            .react-datepicker__input-container {
                > input {
                    display: flex;
                    text-align: start;
                    height: 30px !important;
                    width: 150px !important;
                    font-size: 14px;
                    border-radius: 2px;
                    color: ${({ theme }) => theme.gray3};
                    @media (max-width: ${BREAK_POINTS.sm}) {
                        width: 100% !important;
                    }
                }
            }
            @media (max-width: ${BREAK_POINTS.sm}) {
                width: 100%;
            }
        }
    }
    .input-error {
        input {
            border: ${({ theme }) => `1px solid ${theme.dangerMain}`};
        }
    }
`;

const StyledButton = styled.div`
    width: 100%;
    display: flex;
    justify-content: end;
    padding-right: 25px;
    padding-bottom: 25px;

    @media (max-width: ${BREAK_POINTS.sm}) {
        padding-right: 0px;
        margin-top: 20px;
        > button {
            width: fit-content;
        }
    }
`;
const StyledField = styled.div`
    display: flex;
    gap: 20px;
    margin-top: 20px;

    .gi-input,
    .gi-component,
    .grid-row {
        display: flex;
        justify-content: center;
        margin-top: 0 !important;

        .gi-input__label,
        .usa-legend {
            width: 200px !important;
            color: ${({ theme }) => theme.gray2};
            letter-spacing: 0;
            float: left;
            font-size: 1.6rem;
            padding: 0 1rem;
            text-align: right;
            font-weight: 500;
        }


        @media (max-width: 768px) {
            flex-direction: column;
            justify-content: center;

            .gi-input__input,
            .gi-input__label,
            .usa-legend,
            input,
            select,
            .usa-list,
            .react-datepicker-wrapper {
                min-width: 100%;
                text-align: left;
            }
        }
    }
    .date-picker,
    .shown.error--msg {
        margin-top: 0 !important;
        padding: 8px;
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
    }

    .gi-dropdown {
        margin: 0;
    }
`;

const ContactInformation = () => {
    const dispatch: Dispatch<any> = useAppDispatch();
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const environment = useAppSelector(environmentSelector);
    const { stateCode, user, isState = {} } = environment;
    const isAdminUser = user?.userActiveRole.toUpperCase() !== ALL_USER_ROLES.INDIVIDUAL;

    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo);
    const errorMsg = useAppSelector(state => state.ridp.responseBasedErrorMessage);
    const [userInfo, setUserInfo] = useState<IUserInfo>(currentUserInfo as IUserInfo);
    const ridpPrimaryResponse = useAppSelector(state => state.ridp.ridpPrimaryResponse);
    const isRidpRbaEnabled = currentUserInfo?.isRidpRbaEnabled === true;
    // SSN/DOB mandatory for admin only in new RBA flow; remain optional in legacy RIDP
    const isSsnDobRequired = isAdminUser && isRidpRbaEnabled;
    const responseCode = isRidpRbaEnabled ? FinalDecisionCodes.INVESTIGAT : ResponseCodes.HS000000;
    const [errors, setErrors] = useState({
        personGivenName: false,
        personSurName: false,
        streetName: false,
        locationCityName: false,
        locationStateUSPostalServiceCode: false,
        locationPostalCode: false,
        personBirthDate: false,
        personSSNIdentification: false,
        emailAddress: false
    });
    const [errorMessage, setErrorMessage] = useState('');
    const formState = useAppSelector(state => state.ssap.form['ridpUserInfo']);
    const {
        routes: { RIDP_VERIFICATION_COMPLETE },
    } = configs;

    const {
        personGivenName,
        personMiddleName,
        personBirthDate,
        personNameSuffixText,
        personSurName,
        streetName,
        emailAddress,
        fullTelephoneNumber,
        locationCityName,
        locationStateUSPostalServiceCode,
        locationPostalCode,
        personSSNIdentification = '', //set SSN as empty string
    } = userInfo;

    const intlTagFormatter = useIntl();

    const caseNumber = params.get('caseNumber');
    const coverageYear = params.get('coverageYear');
    const applicationType = params.get('applicationType');

    const [experianData, setExperianData] = useState({
        jscTimestamp: '',
        jscPayload: '',
        hdimPayload: ''
    });

    const onValidated = (payload: string) => {
        const timestamp = new Date().toISOString();
        setExperianData(prev => ({
            ...prev,
            jscTimestamp: timestamp,
            jscPayload: payload
        }));
    };

    const onInitialized = (hdimPayload: string) => {
        const timestamp = new Date().toISOString();
        setExperianData(prev => ({
            ...prev,
            jscTimestamp: timestamp,
            hdimPayload: hdimPayload
        }));
    };

    useJSCCollector({ onInitialized, onValidated, isRidpRbaEnabled });

    const onFieldChange = (...args) => {
        const [newData] = args;
        setUserInfo({ ...newData });
    };

    useEffect(() => {
        if (userInfo.verifyStatus === 'completed') {
            navigate(
                `${RIDP_VERIFICATION_COMPLETE}?caseNumber=${caseNumber}&applicationType=${applicationType}&coverageYear=${coverageYear}`,
            );
            return;
        }
        validateField();
    }, [userInfo]);

    useEffect(() => {
        setUserInfo(currentUserInfo as IUserInfo);
    }, [currentUserInfo, setUserInfo]);

    const validateField = useCallback(() => {
        Object.keys(errors).forEach(errorVar => {
            setErrors(error => {
                const skipRequired = (!isSsnDobRequired && ['personBirthDate', 'personSSNIdentification'].includes(errorVar)) ||
                    (!isRidpRbaEnabled && errorVar == 'emailAddress');
                return {
                    ...error,
                    [errorVar]: skipRequired ? false : !userInfo[errorVar]?.trim(),
                };
            });
        });
    }, [errors, setErrors, userInfo, isSsnDobRequired]);

    const submitContactInformationHandler = event => {
        event.preventDefault();
        dispatch(submitForm('ridpUserInfo'));
        let isFormValid = true;
        if (formState && formState.registeredInputs) {
            isFormValid = formState.registeredInputs.every(input => input.inputIsValid);
        }
        const hasLocalErrors = Object.keys(errors).some(value => errors[value]);
        if (isFormValid && !hasLocalErrors) {
            if (isRidpRbaEnabled) {
                const dateOfBirth = parseUTCDateWithFormat(userInfo.personBirthDate, MM_SLASH_DD_SLASH_YYYY).format(YYYY_MM_DD);

                const finalPayload = {
                    person: {
                        firstName: userInfo.personGivenName,
                        middleName: userInfo.personMiddleName,
                        lastName: userInfo.personSurName,
                        suffix: userInfo.personNameSuffixText,
                        dateOfBirth,
                        socialSecurityNumber: userInfo.personSSNIdentification,
                    },
                    contactInformation: {
                        streetName: userInfo.streetName,
                        cityName: userInfo.locationCityName,
                        usStateCode: userInfo.locationStateUSPostalServiceCode,
                        zipCode: userInfo.locationPostalCode,
                        emailAddress: userInfo.emailAddress,
                        phoneNumber: userInfo.fullTelephoneNumber,
                    },
                    jscTimestamp: experianData.jscTimestamp || new Date().toISOString(),
                    jscPayload: experianData.jscPayload || document.getElementById('user_dsh')?.value,
                    hdimPayload: experianData.hdimPayload || document.getElementById('dsh_prefs2')?.value
                };
                dispatch(validateRidpRba(endpoints.RIDP_VALIDATE_RBA_ENDPOINT, finalPayload, responseCode));
            } else {
                const legacyPayload = {
                    personGivenName: userInfo.personGivenName,
                    personMiddleName: userInfo.personMiddleName,
                    personSurName: userInfo.personSurName,
                    personNameSuffixText: userInfo.personNameSuffixText,
                    personBirthDate: userInfo.personBirthDate,
                    personSSNIdentification: userInfo.personSSNIdentification,
                    streetName: userInfo.streetName,
                    locationCityName: userInfo.locationCityName,
                    locationStateUSPostalServiceCode: userInfo.locationStateUSPostalServiceCode,
                    locationPostalCode: userInfo.locationPostalCode,
                    fullTelephoneNumber: userInfo.fullTelephoneNumber,
                    emailAddress: userInfo.emailAddress,
                };
                dispatch(validateRidpPrimary(endpoints.RIDP_VALIDATE_PRIMARY_ENDPOINT, legacyPayload, ResponseCodes.HS000000));
            }
        }
    };

    const handleDateChange = (date: string | null) => {
        const formattedDate = formatDateToMMDDYYYY(date);
        setUserInfo({ ...userInfo, personBirthDate: formattedDate || '' });
    };

    useHandleRIDPResponse(ridpPrimaryResponse, userInfo, setErrorMessage, isRidpRbaEnabled);
    useHandleRIDPRBAResponse(ridpPrimaryResponse, userInfo, setErrorMessage, isRidpRbaEnabled);

    return (
        <main id="main-content" className="gi-panel">
            <StyledContactInformationContainer>
                <StyledIdentityHeader id="contact-information-header" className="h4">
                    <FormattedMessage id="ridp.sidebar.contactInformation" />
                </StyledIdentityHeader>
                <AlertMessage 
                    content={
                        <FormattedMessage 
                            id='ridp.contactinfo.alert.para' 
                            values={{ 
                                br: <br/>,
                                strong: chunks => <strong>{chunks}</strong>
                            }}
                        />
                    }
                    type={'info'} 
                />
                {errorMsg && <ErrorMessage messageText={errorMsg} isVisible={true} />}
                {errorMessage && <ErrorMessage messageText={errorMessage} isVisible={true} />}

                <Form {...{ formName: 'ridpUserInfo', action: {}, formProps: { id: 'ridp_form' } }} aria-labelledby="contact-information-header">
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.firstName',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'personGivenName',
                            value: personGivenName,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.NAME,
                            validationErrorMessage: VALIDATION_ERROR_MESSAGES.FIRST_NAME,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                `${errors.personGivenName ? 'input-error' : ''}`,
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.middleName',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: false,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'personMiddleName',
                            value: personMiddleName,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            helperClasses: ['control-label', 'regular-input'],
                        }}
                        inputActions={{
                            onFieldChange,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.lastName',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'personSurName',
                            value: personSurName,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.NAME,
                            validationErrorMessage: VALIDATION_ERROR_MESSAGES.LAST_NAME,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                `${errors.personSurName ? 'input-error' : ''}`,
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.suffix',
                            isLabelRequired: false,
                        }}
                        inputData={{
                            isInputRequired: false,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'personNameSuffixText',
                            value: personNameSuffixText,
                            options: DROPDOWN_OPTIONS.SUFFIX(stateCode),
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.CHOSEN,
                            inputClass: ['small-width'],
                        }}
                        inputActions={{
                            onFieldChange,
                        }}
                    />

                    <ValidationErrorMessage
                        isVisible={errors.personBirthDate}
                        messageText={VALIDATION_ERROR_MESSAGES.DATE}
                        errorClasses={['desktop:grid-offset-2']}
                    />
                    <StyledField>
                        <DatePicker
                            labelText="ridp.contactinfo.input.label.dob"
                            wrapperClasses={['grid-row', 'gi-component', 'gi-dropdown', 'date-picker']}
                            className={errors.personBirthDate ? 'input-error' : ''}
                            id="ridpDatepicker"
                            name="ridpDatepicker"
                            type="text"
                            placeholder="MM/DD/YYYY"
                            showIcon={false}
                            pattern="MM/DD/YYYY"
                            value={personBirthDate ? new Date(personBirthDate) : ''}
                            withMask
                            isRequired={isSsnDobRequired}
                            showYearDropdown={false}
                            onDateChange={value => handleDateChange(value || '')}
                        />
                    </StyledField>
                    <UniversalInput
                        key={currentUserInfo?.simplifiedSsnValidation} 
                        label={{
                            text: 'ridp.contactinfo.input.label.ssn',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: isSsnDobRequired,
                            type: INPUT_TYPES.SSN_INPUT,
                            name: 'personSSNIdentification',
                            value: personSSNIdentification,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: currentUserInfo?.simplifiedSsnValidation ? VALIDATION_TYPES.SSN_SIMPLIFIED : VALIDATION_TYPES.SSN,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                'small-width',
                                `${errors.personSSNIdentification ? 'input-error' : ''}`
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.address',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'streetName',
                            value: streetName,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.ADDRESS_LINE1,
                            valuesToMatch: isState,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                `${errors.streetName ? 'input-error' : ''}`,
                                'input-mobile',
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.city',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'locationCityName',
                            value: locationCityName,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.CITY,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                `${errors.locationCityName ? 'input-error' : ''}`,
                                'input-mobile',
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.state',
                            isLabelRequired: false,
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.DROPDOWN,
                            name: 'locationStateUSPostalServiceCode',
                            value: locationStateUSPostalServiceCode,
                            options: DROPDOWN_OPTIONS.STATE,
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.CHOSEN,
                            validationErrorMessage: VALIDATION_ERROR_MESSAGES.STATE,
                            inputClass: [
                                `${errors.locationStateUSPostalServiceCode ? 'input-error' : ''}`,
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.zipCode',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: true,
                            type: INPUT_TYPES.REGULAR_INPUT,
                            name: 'locationPostalCode',
                            value: locationPostalCode,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            validationType: VALIDATION_TYPES.ZIP_CODE,
                            validationErrorMessage: VALIDATION_ERROR_MESSAGES.ZIP_CODE,
                            helperClasses: [
                                'control-label',
                                'regular-input',
                                'small-width',
                                `${errors.locationPostalCode ? 'input-error' : ''}`,
                            ],
                        }}
                        errorClasses={['desktop:grid-offset-2']}
                        inputActions={{
                            onFieldChange,
                            onFieldBlur: validateField,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    <UniversalInput
                        label={{
                            text: 'ridp.contactinfo.input.label.phone',
                            labelClasses: ['spl__width--01'],
                        }}
                        inputData={{
                            isInputRequired: false,
                            type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                            name: 'fullTelephoneNumber',
                            value: fullTelephoneNumber,
                            placeholder: '',
                            currentData: userInfo,
                            fields: [],
                            isInputStandalone: true,
                            helperClasses: ['control-label', 'regular-input', 'small-width'],
                            validationType: VALIDATION_TYPES.PHONE_NUMBER,
                        }}
                        inputActions={{
                            onFieldChange,
                        }}
                        parentForm={{ name: 'ridpUserInfo' }}
                    />
                    {isRidpRbaEnabled && (
                        <UniversalInput
                            label={{
                                text: 'ssap.input.label.email',
                                labelClasses: ['spl__width--01'],
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.REGULAR_INPUT,
                                validationType: VALIDATION_TYPES.EMAIL,
                                name: 'emailAddress',
                                value: emailAddress,
                                placeholder: '',
                                currentData: userInfo,
                                fields: [],
                                isInputStandalone: true,
                                helperClasses: [
                                    'control-label',
                                    'regular-input',
                                    `${errors.emailAddress ? 'input-error' : ''}`,
                                ],
                            }}
                            errorClasses={['desktop:grid-offset-2']}
                            inputActions={{
                                onFieldChange,
                                onFieldBlur: validateField,
                            }}
                            parentForm={{ name: 'ridpUserInfo' }}
                        />
                    )}
                    <StyledButton>
                        <GIButton
                            type="submit"
                            id="ridpContactInfo"
                            labelId="ridp.contactinfo.input.label.continue"
                            onClickHandler={e => submitContactInformationHandler(e)}
                            data-testid={'ridpContactInfo'}
                        />
                    </StyledButton>
                </Form>
            </StyledContactInformationContainer>
        </main>
    );
};

export default ContactInformation;
